import { Client, ClientMock, DEFAULT_LOADABLE_STATE, Loadable, LoadableMock, RecursivePartial } from '@aiwyn/shared';
import { PayableUi, PayableUiMock } from '../../../api/models/invoice.models';
import {
  ArTranAllocation,
  ArTranAllocationMock,
  BaseJob,
  BaseJobMock,
  BaseLateFee,
  BaseLateFeeMock,
  Credit,
  CreditLabel,
  CreditLabelMock,
  CreditMock,
  OpenPaymentLinkResult,
  PaymentActionRequirement,
  PaymentFeeConfig,
  PaymentFeeConfigMock,
  PaymentMethodType,
  PaymentPlatform,
} from '@aiwyn/api';
import { PaymentMethodMode } from '../platforms';
import { ExecutedPaymentBatches, PayOpenPortalPayable, PayWithoutInvoicePayable, PayWithoutInvoicePayableMock } from '../models';
import { AddOn, AddOnOption, AddOnOptionMock } from '../../../model';
import { PaymentSurface } from '../tracking';
import { merge } from 'ts-deepmerge';

export interface PaymentsStateModel {
  activePlatform: Loadable<PaymentPlatform>;

  // payment method
  paymentMethodMode?: PaymentMethodMode;
  newPaymentMethodType?: PaymentMethodType;
  shouldSaveNewPaymentMethod?: boolean;
  shouldSetNewPaymentMethodAsDefault?: boolean;

  // prepare payment
  lateFees: Loadable<BaseLateFee[]>;
  payAllLateFees?: boolean; // only relevant when late fees aren't grouped by client (stays true after payment to accommodate multicurrency)
  payLateFeeClientIds?: number[]; // client IDs with late fees applied
  paymentFeeConfigs: Loadable<PaymentFeeConfig[]>;

  // executed payment
  executedPaymentBatches?: ExecutedPaymentBatches;
  currentPaymentActionRequirement?: PaymentActionRequirement;
  // UUID minted at MakePayment dispatch, cleared when the batch settles. Echoed onto every workflow
  // event fired during the make-payment lifecycle so charges can be joined to their outcomes
  // and retries can be counted (distinct IDs per session).
  currentPaymentBatchId?: string;

  arTranAllocations?: ArTranAllocation[];
  payables: Loadable<PayableUi[]>;

  creditLabels: Loadable<CreditLabel[]>;
  credits: Loadable<Credit[]>;
  // Client IDs with credits applied
  creditAppliedClientIds?: number[];

  addOnOptions: Loadable<AddOnOption[]>;
  addOns?: AddOn[];

  payWithInvoices?: boolean;
  payWithoutInvoicePayable?: Partial<PayWithoutInvoicePayable | PayOpenPortalPayable>;
  jobs: Loadable<BaseJob[]>;
  clients: Loadable<Client[]>;

  customProcessingFeeRate?: number;

  providedPrimaryRecipientEmail?: string;
  additionalRecipientEmails?: string[];
  quickPaymentLinkId: Loadable<number>;
  quickPaymentLinkArgs?: OpenPaymentLinkResult;

  // PP-3775: explicit entry-source override consulted by resolvePaymentSurface() only on the ambiguous
  // /reviewAndPay branch, where the pay-invoice email link and the in-portal "Review Invoices" button
  // share the same URL. Set at each entry chokepoint; undefined falls back to pure URL classification.
  paymentEntrySource?: PaymentSurface;
}

export const DEFAULT_PAYMENTS_STATE_MODEL: PaymentsStateModel = {
  activePlatform: DEFAULT_LOADABLE_STATE,
  payables: DEFAULT_LOADABLE_STATE,
  creditLabels: DEFAULT_LOADABLE_STATE,
  credits: DEFAULT_LOADABLE_STATE,
  addOnOptions: DEFAULT_LOADABLE_STATE,
  lateFees: DEFAULT_LOADABLE_STATE,
  paymentFeeConfigs: DEFAULT_LOADABLE_STATE,
  jobs: DEFAULT_LOADABLE_STATE,
  clients: DEFAULT_LOADABLE_STATE,
  quickPaymentLinkId: DEFAULT_LOADABLE_STATE,
};

export class PaymentsStateModelMock {
  protected _model: PaymentsStateModel = {
    activePlatform: new LoadableMock(PaymentPlatform.STRIPE).model(),
    paymentMethodMode: PaymentMethodMode.SAVED,
    newPaymentMethodType: undefined,
    shouldSaveNewPaymentMethod: undefined,
    shouldSetNewPaymentMethodAsDefault: undefined,
    payAllLateFees: false,
    lateFees: new LoadableMock([new BaseLateFeeMock().model()]).model(),
    paymentFeeConfigs: new LoadableMock([new PaymentFeeConfigMock().model()]).model(),
    executedPaymentBatches: undefined,
    arTranAllocations: [new ArTranAllocationMock().model()],
    payables: new LoadableMock<PayableUi[]>([
      // invoice (default)
      new PayableUiMock().model(),

      // prepayment (can't be partially paid)
      new PayableUiMock({ payable: { arTran: { id: 2, amount: 3000.25, allocated: 0 } } }, 'prepayment').model(),

      // partially paid invoice
      new PayableUiMock({ payable: { arTran: { id: 3, amount: 2000, allocated: 1 } } }).model(),
    ]).model(),
    creditLabels: new LoadableMock([new CreditLabelMock().model()]).model(),
    credits: new LoadableMock([new CreditMock().model()]).model(),
    addOnOptions: new LoadableMock([new AddOnOptionMock().model()]).model(),
    addOns: undefined,
    payWithInvoices: true,
    payWithoutInvoicePayable: new PayWithoutInvoicePayableMock().model(),
    jobs: new LoadableMock([new BaseJobMock().model]).model(),
    clients: new LoadableMock([new ClientMock().model()]).model(),
    quickPaymentLinkId: new LoadableMock<number>(1).model(),
    customProcessingFeeRate: undefined,
    providedPrimaryRecipientEmail: undefined,
    additionalRecipientEmails: undefined,
  };

  constructor(overrides: RecursivePartial<PaymentsStateModel> = {}) {
    this._model = merge.withOptions(
      { mergeArrays: false },
      this._model,
      overrides,
    ) as unknown as PaymentsStateModel;
  }

  public model(): PaymentsStateModel {
    return this._model;
  }
}
